import { useMemo } from "react";

import { breakdown, formatStats, type CommandSource, type Stats } from "../lib/latency";
import { useLatency } from "../store/useLatency";
import { useOrders } from "../store/useOrders";
import { Empty, Panel } from "./Panel";

/**
 * Разбор задержки команд — артефакт приёмки вехи M5.
 *
 * КАЖДАЯ строка называет свои часы, и это не педантизм. Правило M3 запрещает
 * вычитать отметки времени с разных часов; здесь оно нужно в уточнённом виде:
 * вычитать ДЛИТЕЛЬНОСТИ, измеренные каждая своими часами, можно — смещение
 * сокращается, остаётся расхождение хода в единицы ppm. Именно поэтому строка
 * «остальное» законна, а `command.accepted_at` в арифметике не участвует вовсе.
 *
 * Порогов и цветов нет намеренно: критерий вехи — «измерена и стабильна», а не
 * «меньше X». Выдумать порог до того, как собраны данные, значит красить панель
 * в тревожный цвет неделю подряд. Вместо порога показан РАЗБРОС (min…max) —
 * стабильность видна именно по нему.
 */
export function LatencyPanel() {
  const samples = useLatency((s) => s.samples);
  const pending = useOrders((s) => s.pending);

  const all = useMemo(() => breakdown(samples), [samples]);
  const bySource = useMemo(
    () =>
      (["form", "quick", "hotkey"] as CommandSource[]).map((source) => ({
        source,
        stats: breakdown(samples, source),
      })),
    [samples],
  );

  // Команды без ответа не дают замера по построению — а это ровно худшие
  // случаи, и молча терять их значит льстить p95.
  const unanswered = Object.values(pending).filter((p) => p.timedOut).length;

  return (
    <Panel
      title="Задержка команд"
      subtitle={`n=${all.roundTrip.n}`}
      actions={
        <span className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
          без ответа {unanswered} · отклонено {all.rejected}
          {/* Кольцо живёт всю жизнь вкладки и ничего не забывает. Это верно для
              приёмки — квантили обязаны считаться по всей выборке, — но после
              разового эпизода связи оператор до перезагрузки страницы видел бы
              его в каждой строке. Сброс существует ровно для этого. */}
          {all.roundTrip.n > 0 && (
            <button
              type="button"
              onClick={() => useLatency.getState().clear()}
              className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-accent)]"
              title="забыть накопленные замеры и начать выборку заново"
            >
              сбросить
            </button>
          )}
        </span>
      }
    >
      {all.roundTrip.n === 0 ? (
        <Empty>
          Замеров пока нет: отправьте ордер, и здесь появится разбор пути «клик → вердикт».
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          <table className="w-full text-xs">
            <thead className="text-[var(--color-muted)]">
              <tr>
                <th className="pb-1 text-left font-normal">этап</th>
                <th className="pb-1 text-right font-normal">p50 / p95</th>
                <th className="pb-1 text-right font-normal">разброс</th>
                <th className="pb-1 pl-3 text-left font-normal">часы</th>
              </tr>
            </thead>
            <tbody>
              <Row
                label="клик → 202 от web_api"
                stats={all.toAccepted}
                clock="браузер, монотонные"
              />
              <Row
                label="клик → вердикт в сокете"
                stats={all.roundTrip}
                clock="браузер, монотонные"
              />
              <Row
                label="внутри трейдера (стрим → CLOB)"
                stats={all.trader}
                clock="ТРЕЙДЕР (command_result.latency_ms)"
              />
              <Row
                label="остальное (оценка)"
                stats={all.overhead}
                clock="разность ДЛИТЕЛЬНОСТЕЙ, не отметок"
              />
            </tbody>
          </table>

          <p className="text-xs text-[var(--color-muted)]">
            «клик → вердикт» — ВЕРХНЯЯ граница пути «клик → POST в CLOB»: в неё входит и обратная
            дорога вердикта по сокету. Нижнюю границу трейдер публикует нотой{" "}
            <span className="num">place_total_ms</span>.
          </p>

          {/* Куда смотреть, когда число большое. Разбор 13.08.2026: оператор
              увидел здесь десятки секунд, а сервер в тот же день отвечал за
              41–50 мс на каждой команде — весь путь лежал между браузером и
              web_api. Без этой подсказки разделить эти два случая было нечем. */}
          <p className="text-xs text-[var(--color-muted)]">
            Раздулось «клик → 202» — дело в канале до web_api; раздулось только «клик → вердикт» —
            в обратной дороге по сокету. Серверную часть пути видно нотами{" "}
            <span className="num">place_total_ms</span> и{" "}
            <span className="num">clob_order_latency_ms</span> в <span className="num">/healthz</span>{" "}
            трейдера: они меряют то же самое его часами.
          </p>

          {all.negativeOverhead > 0 && (
            <p className="text-xs text-[var(--color-muted)]">
              отброшено замеров с отрицательной разностью: {all.negativeOverhead} — трейдер намерил
              больше браузера, это факт о развёртывании, а не о задержке
            </p>
          )}

          <div className="flex flex-wrap gap-3 text-xs text-[var(--color-muted)]">
            {bySource.map(({ source, stats }) => (
              <span key={source} className="num">
                {sourceLabel(source)}: {formatStats(stats.roundTrip)} мс (n={stats.roundTrip.n})
              </span>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function Row({ label, stats, clock }: { label: string; stats: Stats; clock: string }) {
  return (
    <tr>
      <td className="py-0.5">{label}</td>
      <td className="num py-0.5 text-right">{formatStats(stats)}</td>
      <td className="num py-0.5 text-right text-[var(--color-muted)]">
        {stats.min === null || stats.max === null
          ? "—"
          : `${Math.round(stats.min)}…${Math.round(stats.max)}`}
      </td>
      <td className="py-0.5 pl-3 text-[var(--color-muted)]">{clock}</td>
    </tr>
  );
}

function sourceLabel(s: CommandSource): string {
  switch (s) {
    case "form":
      return "из формы";
    case "quick":
      return "мышью";
    case "hotkey":
      return "с клавиши";
  }
}
